import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {AppConfigService} from '../../common/service/app-config.service';
import {catchError, map, Observable, of, throwError} from 'rxjs';
import {PlanPdfService} from '../../common/service/plan-pdf.service';
import {AttachmentModel} from 'src/app/common/models/attachment-model';
import {AlertModalService} from '../component/modal/alert-modal/alert-modal.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {ATTACHMENT_PLAN_PDF, PDF_CREATION_METHOD} from '../../common/constants';

@Injectable({
    providedIn: 'root'
})
export class AttachmentValidationService {
    title = 'Attachment Validation Service';

    attachmentValidationInfoMap = {};

    constructor(
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private alertModalService: AlertModalService,
        private planPdfService: PlanPdfService,
        private planModelService: PlanModelService
    ) {}

    validateAttachment(attachment: AttachmentModel, attachmentIndex: string, newFileInformation: AttachmentModel): Observable<any> {
        if (attachment.attachmentType === ATTACHMENT_PLAN_PDF) {
            if (this.isLandgateSigned()) {
                return this.validatePlanPdf(attachment, attachmentIndex, newFileInformation);
            } else {
                this.updateFileInformation(attachment, newFileInformation);
            }
        } else {
            return this.validate(newFileInformation.uuid, attachment).pipe(
                map(result => {
                    this.updateFileInformation(attachment, newFileInformation);
                    return result;
                })
            );
        }
        return of({result: ''});
    }

    validatePlanPdf(attachment: AttachmentModel, attachmentIndex: string, newFileInformation: AttachmentModel): Observable<any> {
        return this.planPdfService.validatePlanPdf(newFileInformation.uuid, attachment).pipe(
            map(result => {
                if (result.error) {
                    this.clearFileInformation(attachment);
                } else {
                    this.updateFileInformation(attachment, newFileInformation);
                }
                return result;
            }),
            catchError(error => {
                return throwError(() => {
                    new Error(error);
                });
            })
        );
    }

    validate(fileUuid: string, attachment: AttachmentModel): Observable<any> {
        console.debug(`${this.title}: validateAttachment`);
        const validateCsdUrl = this.appConfigService.settings.apiGatewayUrl + '/attachment/validateAndCalculate';
        const request = {
            fileGuid: fileUuid,
            attachmentType: attachment.attachmentType
        };

        return this.http.post<any>(validateCsdUrl, request).pipe(
            map(validateAndLotCountResponse => {
                this.attachmentValidationInfoMap[attachment.attachmentType] = {
                    errors: validateAndLotCountResponse.validationResponse.errors.slice(),
                    warnings: validateAndLotCountResponse.validationResponse.warnings.slice(),
                    totalLotCount: validateAndLotCountResponse.totalPlanLots,
                    totalChargeableLotCount: validateAndLotCountResponse.totalChargeableLots
                };
                return validateAndLotCountResponse;
            }),
            catchError(error => {
                console.error(`Error validating the uploaded file ${error}`);
                const errorMessage = 'System error while validating the attached file';
                this.alertModalService.createAlertMessage(errorMessage);
                return throwError(() => {
                    new Error(error);
                });
            })
        );
    }

    updateFileInformation(attachment: AttachmentModel, newFileInformation: AttachmentModel) {
        this.setFileDetails(attachment, newFileInformation);
    }

    clearFileInformation(attachment: AttachmentModel) {
        if (attachment) {
            const defaultFileInformation: AttachmentModel = {
                uuid: undefined,
                fileName: undefined,
                fileSize: undefined
            };

            this.setFileDetails(attachment, defaultFileInformation);
        }
    }
    setFileDetails(attachment: AttachmentModel, newFileInformation: AttachmentModel) {
        attachment.fileLocation = newFileInformation.uuid;
        attachment.fileName = newFileInformation.fileName;
        attachment.fileSize = newFileInformation.fileSize;
    }
    clearValidationInfoattachmentType() {}
    clearAllValidationInfo() {
        Object.keys(this.attachmentValidationInfoMap).forEach(k => delete this.attachmentValidationInfoMap[k]);
    }

    getWarnings(attachmentType: string): string[] {
        let attachmentValidationInfo = this.attachmentValidationInfoMap[attachmentType];
        return attachmentValidationInfo ? attachmentValidationInfo.warnings : [];
    }

    getTotalLotCount(attachmentType: string): number {
        let attachmentValidationInfo = this.attachmentValidationInfoMap[attachmentType];
        return attachmentValidationInfo ? attachmentValidationInfo.totalLotCount : 0;
    }

    getTotalChargeableLotCount(attachmentType: string): number {
        let attachmentValidationInfo = this.attachmentValidationInfoMap[attachmentType];
        return attachmentValidationInfo ? attachmentValidationInfo.totalChargeableLotCount : 0;
    }

    /**
     * Whether attaching the file makes the plan a CAD plan: the Plan PDF is not one the system produced, so the plan is no
     * longer created from the Surveyor Portal or as a Digital Plan. A lodged plan keeps its PDF creation method, as a PDF
     * attached to it is an additional document rather than the plan itself.
     */
    requiresCadPlanConversion(attachment: AttachmentModel): boolean {
        const {pdfCreationMethod} = this.planModelService.getModel();
        return (
            attachment.attachmentType === ATTACHMENT_PLAN_PDF &&
            [PDF_CREATION_METHOD.SURVEYOR_PORTAL, PDF_CREATION_METHOD.DIGITAL_PLAN].includes(pdfCreationMethod) &&
            !this.isLandgateSigned() &&
            !this.isAdditional()
        );
    }

    private isLandgateSigned() {
        return !this.planModelService.isNotLandgateSigned;
    }

    private isAdditional(): boolean {
        return 'LODGED' === this.planModelService.getModel().planLodgementStage.toUpperCase();
    }

    hasCSDorCSVAttachment(): boolean {
        return Object.keys(this.attachmentValidationInfoMap).some(key => ['CADASTRAL_SURV', 'LAND_CSV'].includes(key));
    }

    hasValidationInfo(attachmentType: string): boolean {
        return !!this.attachmentValidationInfoMap[attachmentType];
    }

    getErrors(attachmentType: string): string[] {
        let attachmentValidationInfo = this.attachmentValidationInfoMap[attachmentType];
        return attachmentValidationInfo ? attachmentValidationInfo.errors : [];
    }

    clearValidationInfo(attachmentType: any) {
        this.attachmentValidationInfoMap[attachmentType] = {
            errors: [],
            warnings: [],
            totalLotCount: 0
        };
    }
}
